import React from "react";
import { MessageCircle, Clock, AlertCircle } from "lucide-react";
import { tr } from "../lib/i18n";

/**
 * Prominent alert shown at the end of Spa / Massage / Horse booking flows
 * to make explicit that the booking is NOT confirmed until we send a
 * WhatsApp with the exact time. Prevents "no-show" ambiguity.
 */
export function PendingConfirmationAlert({ lang, phone, variant = "success" }) {
  const isSuccess = variant === "success";
  return (
    <div
      data-testid="pending-confirmation-alert"
      className="mt-5 rounded-2xl border-2 border-[#c48b31] bg-gradient-to-br from-[#fff9ef] to-[#fdf1dd] p-4 sm:p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#25D366] opacity-20 animate-ping" />
            <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white">
              <MessageCircle className="h-5 w-5" />
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4 text-[#c48b31]" />
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#c48b31]">
              {tr({
                it: "Attenzione — Prenotazione in attesa",
                en: "Heads up — booking pending",
                es: "Atención — reserva pendiente",
                fr: "Attention — réservation en attente",
                de: "Achtung — Buchung ausstehend",
              }, lang)}
            </div>
          </div>
          <h3 className="mt-1 font-serif text-lg sm:text-xl font-bold text-[#2b1410] leading-tight">
            {tr({
              it: "La prenotazione NON è ancora confermata",
              en: "Your booking is NOT confirmed yet",
              es: "Tu reserva AÚN NO está confirmada",
              fr: "Votre réservation N'EST PAS ENCORE confirmée",
              de: "Deine Buchung ist NOCH NICHT bestätigt",
            }, lang)}
          </h3>
          <p className="mt-2 text-sm text-[#2b1410] leading-relaxed">
            {tr({
              it: (
                <>
                  Ti contatteremo su <strong className="text-[#25D366]">WhatsApp</strong> entro poche ore per confermare
                  l'orario preciso e darti tutte le info. La prenotazione è valida solo{" "}
                  <strong>dopo aver ricevuto il nostro messaggio</strong>.
                </>
              ),
              en: (
                <>
                  We'll reach out on <strong className="text-[#25D366]">WhatsApp</strong> within a few hours to confirm
                  the exact time and share all info. The booking becomes valid{" "}
                  <strong>only after our message</strong>.
                </>
              ),
              es: (
                <>
                  Te contactaremos por <strong className="text-[#25D366]">WhatsApp</strong> en pocas horas para
                  confirmar la hora exacta. La reserva será válida solo{" "}
                  <strong>tras recibir nuestro mensaje</strong>.
                </>
              ),
              fr: (
                <>
                  Nous vous contacterons sur <strong className="text-[#25D366]">WhatsApp</strong> pour confirmer
                  l'heure exacte. La réservation n'est valide{" "}
                  <strong>qu'après notre message</strong>.
                </>
              ),
              de: (
                <>
                  Wir melden uns per <strong className="text-[#25D366]">WhatsApp</strong>, um die genaue Uhrzeit zu
                  bestätigen. Die Buchung gilt <strong>erst nach unserer Nachricht</strong>.
                </>
              ),
            }, lang)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/70 border border-[#e8dcc8] px-2 py-1 text-[#7c5a4e]">
              <Clock className="h-3 w-3" />
              {tr({
                it: "Risposta entro poche ore",
                en: "Reply within a few hours",
                es: "Respuesta en pocas horas",
                fr: "Réponse sous quelques heures",
                de: "Antwort in wenigen Stunden",
              }, lang)}
            </span>
            {phone && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/70 border border-[#e8dcc8] px-2 py-1 text-[#7c5a4e]">
                <MessageCircle className="h-3 w-3" />
                {phone}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
